import React, { useCallback, useState } from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { CloudKeyModel } from 'src/Libs/installation-rights-management/models'
import { Box, IconButton, Menu, MenuItem } from '@mui/material'
import { Dotsmenu } from '@e3dc-react/icons'
import useModals from 'src/Hooks/useModals'
import { OptionalTooltipWrapper } from '@e3dc-react/shell/Elements/Tooltip'
import { SERVICE_LEVELS_ALL } from 'src/Configuration'
import { useTypedSelector } from 'src/State/RootReducer'
import { ExtensionsType } from 'src/Libs/api/types'

interface Props {
  item: CloudKeyModel
  onRemove: (value: CloudKeyModel) => void
  onRename: (value: CloudKeyModel, description: string) => void
  disable: boolean
  tooltipText?: string
}

const ActionMenu: React.VFC<Props> = ({ disable = false, tooltipText = '', item, onRemove, onRename }) => {
  const { t } = useCustomTranslation()
  const { openConfirmationModal, openSimpleTextDialog } = useModals()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const userLevel = useTypedSelector(state => state.user.userData?.level)
  const isRestrictedToServiceLevel = disable && !SERVICE_LEVELS_ALL.includes(userLevel || 'USER')
  const open = Boolean(anchorEl)
  const handleClick = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget)
  }
  const closeMenu = (): void => {
    setAnchorEl(null)
  }
  const processRemove = useCallback(
    (close: () => void): void => {
      onRemove(item)
      close()
    },
    [onRemove, item]
  )
  const processRenameKey = useCallback(
    (value: string, close: () => void): void => {
      onRename(item, value)
      close()
    },
    [onRename, item]
  )
  const handleClose = useCallback((): void => {
    closeMenu()
  }, [])

  const handleRemoveDialog = useCallback((): void => {
    const isLuoxEnergy: boolean = item.scope === ExtensionsType.luoxEnergy
    openConfirmationModal({
      onConfirm: processRemove,
      buttonText: [t('common.close'), t('common.confirm')],
      title: t('installation.cloud-registry-keys.remove-key.confirm.title'),
      text: isLuoxEnergy
        ? t('installation.cloud-registry-keys.remove-key.confirm.Lumenaza-description', { value: `'${item.description}'` })
        : t('installation.cloud-registry-keys.remove-key.confirm.description', { value: `'${item.description}'` }),
    })
    closeMenu()
  }, [openConfirmationModal, processRemove, t, item])

  const handleRenameDialog = useCallback((): void => {
    openSimpleTextDialog({
      title: t('installation.cloud-registry-keys.rename-key.dialog.title'),
      confirmTitle: t('installation.access-and-invitations.dialogs.rename-key-confirm.title'),
      confirmText: t('installation.access-and-invitations.dialogs.rename-key-confirm.description'),
      onConfirm: processRenameKey,
      value: item.description,
    })
    closeMenu()
  }, [openSimpleTextDialog, t, processRenameKey, item.description])
  return (
    <Box>
      <IconButton onClick={handleClick}>
        <Dotsmenu />
      </IconButton>
      <Menu id="action-menu" anchorEl={anchorEl} open={open} onClose={handleClose}>
        <OptionalTooltipWrapper titleKey={isRestrictedToServiceLevel ? tooltipText : ''}>
          <MenuItem onClick={handleRemoveDialog} disabled={isRestrictedToServiceLevel}>
            {t('common.remove')}
          </MenuItem>
          <MenuItem onClick={handleRenameDialog} disabled={disable}>
            {t('common.edit')}
          </MenuItem>
        </OptionalTooltipWrapper>
      </Menu>
    </Box>
  )
}

export default ActionMenu
